import { ReactNode } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { PersonTypeBadge } from "@/components/people/PersonTypeBadge";
import { PersonContactDownloadButton } from "@/components/people/PersonContactDownloadButton";
import { PersonType } from "@/hooks/usePeople";
import { ExternalLink, Mail, Phone } from "lucide-react";

interface PersonHoverCardProps {
  personId: string;
  firstName: string;
  lastName: string;
  personType?: string;
  email?: string | null;
  phone?: string | null;
  children: ReactNode;
}

export function PersonHoverCard({
  personId,
  firstName,
  lastName,
  personType,
  email,
  phone,
  children,
}: PersonHoverCardProps) {
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <a
          href={`/people/${personId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="cursor-pointer hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </a>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72">
        <div className="space-y-2">
          <div>
            <p className="text-sm font-semibold">
              {lastName}, {firstName}
            </p>
            {personType && (
              <PersonTypeBadge
                type={personType as PersonType}
                className="mt-1"
              />
            )}
          </div>
          {(email || phone) && (
            <div className="space-y-1.5 text-sm">
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{email}</span>
                </a>
              )}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  <span>{phone}</span>
                </a>
              )}
            </div>
          )}
          {!email && !phone && (
            <p className="text-xs text-muted-foreground">
              No contact details available
            </p>
          )}
          <div className="flex items-center gap-3 pt-1">
            <a
              href={`/people/${personId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open details
            </a>
            <PersonContactDownloadButton
              firstName={firstName}
              lastName={lastName}
              email={email}
              phone={phone}
              variant="link"
              className="text-xs"
            />
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
